import { Button } from '../index';
import React, { useEffect, useState } from 'react';
import Select from '../Select';
import moment from 'moment-timezone';

import '../global.less';
import './businessHours.scss';

const { Option } = Select;

export interface Duration {
    weekDays: {
        sun: boolean;
        mon: boolean;
        tue: boolean;
        wed: boolean;
        thur: boolean;
        fri: boolean;
        sat: boolean;
    };
    times: { start: number; end: number }[];
}

type Weekday = keyof Duration['weekDays'];

interface BizHoursProps {
    onDurationUpdate: (durations: Duration[]) => void;
    durations?: Duration[];
    addTimeText?: string;
    addDurationText?: string;
    labelText?: string;
}

interface DurationProps {
    selectDuration: (duration: Duration, index: number) => void;
    deleteDuration: (index: number) => void;
    duration: Duration;
    index: number;
    addTimeText: string;
}

const timeOptions: number[] = (() => {
    let t = 0;
    const options = [];
    while (t <= 1440) {
        options.push(t);
        t += 30;
    }
    return options;
})();

const daySelected = (duration: Duration, day: Weekday) => {
    const newDuration = { ...duration };
    newDuration.weekDays[day] = !duration.weekDays[day];
    return newDuration;
};

const startTimeSelected = (duration: Duration, time: number, index: number) => {
    const newDuration = { ...duration };
    const newDurationTime = newDuration.times[index];
    if (newDurationTime) {
        newDurationTime.start = time;
        if (newDurationTime.start > newDurationTime.end) {
            newDurationTime.end = time + 30;
        }
    }
    return newDuration;
};

const endTimeSelected = (duration: Duration, time: number, index: number) => {
    const newDuration = { ...duration };
    const newDurationTime = newDuration.times[index];
    if (newDurationTime) {
        newDurationTime.end = time;
    }
    return newDuration;
};

const newTime = (duration: Duration) => {
    return { ...duration, times: [...duration.times, { start: 540, end: 1020 }] };
};

const removeTime = (duration: Duration, index: number, durationIndex: number) => {
    const newDuration = { ...duration };
    newDuration.times.splice(index, 1);
    return newDuration;
};

const DurationComponent = (props: DurationProps) => {
    const [singleDuration, setSingleDuration] = useState(props.duration);

    useEffect(() => {
        props.selectDuration(singleDuration, props.index);
    }, [singleDuration]);

    useEffect(() => {
        if (singleDuration.times.length < 1) {
            props.deleteDuration(props.index);
        }
    }, [singleDuration.times.length]);

    return (
        <div className="rain-biz-hours-duration-component">
            <div className="rain-biz-hours-duration-selection">
                <div className="rain-biz-hours-day-selection">
                    {(Object.keys(singleDuration.weekDays) as Weekday[]).map((day: Weekday) => {
                        return (
                            <Button
                                key={day}
                                type={singleDuration.weekDays[day] ? 'primary' : 'default'}
                                shape="circle"
                                size="large"
                                onClick={() => setSingleDuration(daySelected(singleDuration, day))}>
                                {day.substr(0, 1).toUpperCase()}
                            </Button>
                        );
                    })}
                </div>
                <div className="rain-biz-hours-time-selection">
                    {singleDuration.times.map((time, index) => {
                        return (
                            <div className="rain-biz-hours-single-time-selection" key={`${time}-${index}`}>
                                <Select
                                    value={time.start}
                                    onChange={(value) =>
                                        setSingleDuration(startTimeSelected(singleDuration, value, index))
                                    }>
                                    {timeOptions.slice(0, -1).map((timeOption) => {
                                        return (
                                            <Option key={`start-${timeOption}-${index}`} value={timeOption}>
                                                {moment().set({ hour: 0, minute: timeOption }).format('h:mm A')}
                                            </Option>
                                        );
                                    })}
                                </Select>
                                <div className="rain-biz-hours-dash">-</div>
                                <Select
                                    value={time.end}
                                    onChange={(value) =>
                                        setSingleDuration(endTimeSelected(singleDuration, value, index))
                                    }>
                                    {timeOptions.slice(1).map((timeOption) => {
                                        return (
                                            <Option key={`start-${timeOption}-${index}`} value={timeOption}>
                                                {moment().set({ hour: 0, minute: timeOption }).format('h:mm A')}
                                            </Option>
                                        );
                                    })}
                                </Select>
                                {(index > 0 || props.index > 0) && (
                                    <Button
                                        type="link"
                                        onClick={() =>
                                            setSingleDuration(removeTime(singleDuration, index, props.index))
                                        }>
                                        x
                                    </Button>
                                )}
                            </div>
                        );
                    })}
                    <Button
                        className="rain-biz-hours-add-time"
                        type="link"
                        onClick={() => setSingleDuration(newTime(singleDuration))}>
                        {props.addTimeText}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const addDuration = (durations: Duration[]) => {
    return [
        ...durations,
        {
            weekDays: {
                sun: false,
                mon: false,
                tue: false,
                wed: false,
                thur: false,
                fri: false,
                sat: false,
            },
            times: [{ start: 540, end: 1020 }],
        },
    ];
};

const BizHours = (props: BizHoursProps) => {
    const [durations, setDurations] = useState<Duration[]>(
        props.durations && props.durations.length > 0
            ? props.durations
            : [
                  {
                      weekDays: {
                          sun: false,
                          mon: false,
                          tue: false,
                          wed: false,
                          thur: false,
                          fri: false,
                          sat: false,
                      },
                      times: [{ start: 540, end: 1020 }],
                  },
              ]
    );

    const { addDurationText = '+ Add Another Duration', addTimeText = '+ Add Time', labelText } = props;

    useEffect(() => {
        props.onDurationUpdate(durations);
    }, [durations]);

    const setSelectedDurations = (duration: Duration, index: number) => {
        const newDurations = [...durations];
        newDurations[index] = duration;
        setDurations(newDurations);
    };

    const deleteDuration = (index: number) => {
        const newDurations = [...durations];
        newDurations.splice(index, 1);
        setDurations(newDurations);
    };

    return (
        <div className="rain-biz-hours">
            {labelText ? <h4>{labelText}</h4> : null}
            <div className="rain-biz-hours-duration">
                {durations.map((duration, index) => {
                    return (
                        <DurationComponent
                            key={index}
                            selectDuration={setSelectedDurations}
                            deleteDuration={deleteDuration}
                            duration={duration}
                            addTimeText={addTimeText}
                            index={index}></DurationComponent>
                    );
                })}
            </div>
            <Button
                className="rain-biz-hours-add-duration"
                type="default"
                onClick={() => setDurations(addDuration(durations))}>
                {addDurationText}
            </Button>
        </div>
    );
};

export default BizHours;
